// Frontend API hooks for the Contract Module.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../../shared/api'

export type ContractStatus =
  | 'no_contract'
  | 'draft'
  | 'ceo1_pending'
  | 'ceo2_pending'
  | 'employee_pending'
  | 'signed'
  | 'void'

export type SignerRole = 'ceo1' | 'ceo2' | 'employee'
export type SignatureMethod = 'touch' | 'pencil' | 'mouse' | 'unknown'
export type SignatureStatus = 'requested' | 'viewed' | 'signed' | 'rejected' | 'expired' | 'revoked'

export interface ContractState {
  vertragId: number
  status: ContractStatus
  template_id: string | null
  blocks_enabled: string[]
  generated_at: string | null
  generated_by: string | null
  signatures: Array<{
    signer_role: SignerRole
    signature_status: SignatureStatus
    signed_at: string | null
    signed_ip: string | null
    signature_method: SignatureMethod | null
    signed_by_supervisor: string | null
    created_at: string
  }>
}

export interface ContractTemplate {
  id: string
  label: string
  anstellungsart: string | null
  firma: string | null
  requires_ceo2: boolean
  template_state: 'draft' | 'reviewed' | 'approved' | 'active' | 'archived'
  version: number
  parent_template_id: string | null
  active_from: string | null
  active_to: string | null
  approved_at: string | null
  approved_by: string | null
  uploaded_at: string
  uploaded_by: string | null
  blob_path: string
}

export interface FirmaSignatory {
  firma: string
  role: SignerRole
  person_name: string
  person_email: string | null
}

// ── State ──────────────────────────────────────────────────────────────

export function useContractState(vertragId: number | null) {
  return useQuery({
    queryKey: ['contracts', 'state', vertragId],
    queryFn: () => api.get<ContractState>(`/api/contracts/${vertragId}/state`),
    enabled: !!vertragId,
    staleTime: 5_000,
  })
}

export function useContractPdfUrl(vertragId: number | null, stage: 'draft' | 'signed' = 'signed') {
  return useQuery({
    queryKey: ['contracts', 'pdf', vertragId, stage],
    queryFn: () => api.get<{ sasUrl: string; blob: string; stage: string }>(`/api/contracts/${vertragId}/pdf?stage=${stage}`),
    enabled: !!vertragId,
    staleTime: 60_000,
  })
}

// ── Mutations ──────────────────────────────────────────────────────────

export function useGenerateContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { vertragId: number; templateId?: string; enabledBlocks?: string[] }) =>
      api.post<{ vertragId: number; blob: string; status: ContractStatus }>('/api/contracts/generate', vars),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['contracts', 'state', vars.vertragId] })
      qc.invalidateQueries({ queryKey: ['contracts', 'pdf', vars.vertragId] })
    },
  })
}

export function useBulkGenerate() {
  return useMutation({
    mutationFn: (vars: { vertragIds: number[]; templateId: string; enabledBlocks?: string[]; label?: string }) =>
      api.post<{ batchId: number; total: number; success: number; failure: number; results: Array<{ vertragId: number; ok: boolean; blob?: string; error?: string }> }>('/api/contracts/generate-bulk', vars),
  })
}

export function useSignContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { vertragId: number; signer_role: SignerRole; signature_png_base64: string; method: SignatureMethod }) =>
      api.post<{ vertragId: number; status: ContractStatus }>(`/api/contracts/${vars.vertragId}/sign`, vars),
    onSuccess: (_r, vars) => {
      qc.invalidateQueries({ queryKey: ['contracts', 'state', vars.vertragId] })
      qc.invalidateQueries({ queryKey: ['contracts', 'pdf', vars.vertragId] })
    },
  })
}

export function useIssueSignToken() {
  return useMutation({
    mutationFn: (vars: { vertragId: number; expiresInDays?: number }) =>
      api.post<{ token: string; link: string; expiresInDays: number }>(`/api/contracts/${vars.vertragId}/sign-token`, { expiresInDays: vars.expiresInDays }),
  })
}

export function useEmailLink() {
  return useMutation({
    mutationFn: (vertragId: number) =>
      api.post<{ link: string; expiresAt: string }>(`/api/contracts/${vertragId}/email-link`, {}),
  })
}

export function useVoidContract() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vertragId: number) => api.post<{ vertragId: number; status: ContractStatus }>(`/api/contracts/${vertragId}/void`, {}),
    onSuccess: (_r, vertragId) => qc.invalidateQueries({ queryKey: ['contracts', 'state', vertragId] }),
  })
}

// ── Templates + Signatories ────────────────────────────────────────────

export function useContractTemplates(state?: string) {
  return useQuery({
    queryKey: ['contracts', 'templates', state],
    queryFn: () => api.get<{ rows: ContractTemplate[] }>(`/api/contracts/templates${state ? `?state=${state}` : ''}`),
    staleTime: 60_000,
  })
}

export function useApproveTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ id: string; state: string }>(`/api/contracts/templates/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contracts', 'templates'] }),
  })
}

export function useFirmaSignatories() {
  return useQuery({
    queryKey: ['contracts', 'firma-signatories'],
    queryFn: () => api.get<{ rows: FirmaSignatory[] }>('/api/contracts/firma-signatories'),
    staleTime: 5 * 60_000,
  })
}
